import { addNewRelicPageAction } from 'ddc-new-relic';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRequestData, usePrefs, useFlags } from 'wsm-common-data';
import { csvToArray } from '../utilities/utility';
import { getPlacement } from '../utilities/aspect';
import { trackPerformanceToNR } from '../utilities/tracking';
import { setIncentives } from '../actions/incentives';
import { setHasData } from '../actions/hasdata';
import { setPersonalized } from '../actions/personalized';
import { proxyServiceProvider } from '../utilities/proxyServiceProvider';
import { aR } from '../global-constants';
import { useURLPrefs } from './useURLPrefs';

const NR_PREFIX = 'PROMOS';
const ENDPOINT_FETCH_ERROR = `${NR_PREFIX}/CLIENT/PROMOS ENDPOINT FETCH ERROR`;

const buildParameters = (prefs, urlPrefs, accountId, locale, pixallId) => {
	const {
		autoGeneratedSpecialLimit,
		enablePersonalization,
		limitByInventory,
		limitByPromotionType,
		limitByTags,
		limitByTagsType,
		'listing.config.id': listingConfigId,
		displayLimit,
		excludeModelOffersWithoutPhotos
	} = prefs;

	const { aspectRatio, enableCarousel, specialCount } = urlPrefs;
	const isCoupon =
		aspectRatio === aR.DESKTOP_4X3 || aspectRatio === aR.MOBILE_4X3_COUPON;

	// Use the url param first if present, otherwise use pref value
	let specialsCountForFilter = specialCount || displayLimit;

	// Resetting displayLimit pref back to the default if the value is 0
	if (displayLimit === '0') {
		specialsCountForFilter = specialCount || '30';
	}

	if (enableCarousel === 'false' && !isCoupon) {
		specialsCountForFilter = '1';
	}

	let autoGeneratedSpecialLimitValue =
		autoGeneratedSpecialLimit === 'No Limit'
			? '30'
			: autoGeneratedSpecialLimit;

	// Resetting autoGeneratedSpecialLimit pref back to the default if the value is false
	if (autoGeneratedSpecialLimit === 'false') {
		autoGeneratedSpecialLimitValue = '3';
	}

	return {
		promoRO: {
			accountId,
			locale,
			autoGeneratedSpecialLimit: autoGeneratedSpecialLimitValue,
			types: csvToArray(limitByPromotionType),
			placements: getPlacement(aspectRatio)
		},
		filterRO: {
			limitByInventory,
			limitByTags: csvToArray(limitByTags),
			limitByTagsType,
			listingConfigId,
			specialCount: specialsCountForFilter,
			excludeModelOffersWithoutPhotos
		},
		eo: {
			pixallId,
			enablePersonalization
		}
	};
};

const usePromos = () => {
	const [promosState, setPromosState] = useState({
		promos: [],
		incentives: [],
		isLoading: true
	});

	const prefs = usePrefs();
	const urlPrefs = useURLPrefs();
	const { locale } = useRequestData();
	const flags = useFlags();
	const proxyService = proxyServiceProvider.getInstance(
		flags['ws-specials-client-timeout'],
		flags['ws-specials-client-retries']
	);

	const { id: accountId } = useSelector((state) => state.account);
	const dispatch = useDispatch();
	const isWindowPresent =
		typeof window !== 'undefined' && Boolean(window.Cookies);
	let pixallId = null;
	if (isWindowPresent) {
		pixallId =
			window.Cookies.get('abc') ?? window.Cookies.get('pxa_id') ?? null;
	}
	const parameters = buildParameters(
		prefs,
		urlPrefs,
		accountId,
		locale,
		pixallId
	);
	const requestUrl = '/api/widget/ws-specials/promos';

	if (typeof window !== 'undefined') {
		// Add NR attribute to check for potential missing cookie
		const pixallCookieIsSet =
			sessionStorage.getItem('pixallCookieIsSet') === 'true';
		const pixallIdMissing = !pixallId && pixallCookieIsSet;
		addNewRelicPageAction('ws-specials-usePromos-fetch', {
			pixallIdMissing
		});
	}

	useEffect(() => {
		proxyService(requestUrl, {
			method: 'POST',
			headers: {
				'content-type': 'application/json;charset=UTF-8'
			},
			body: JSON.stringify({
				parameters
			})
		})
			.then((response) => {
				if (!response) {
					trackPerformanceToNR(
						'ws-specials-promos-fetched-no-response'
					);
					setPromosState({
						promos: [],
						incentives: [],
						isLoading: false
					});
					return;
				}
				const { promos, incentives, personalized } = response;
				dispatch(setPersonalized(personalized));
				dispatch(setHasData(true));

				setPromosState({
					promos,
					incentives,
					isLoading: false
				});
				trackPerformanceToNR('ws-specials-promos-fetched-success');
				dispatch(setIncentives(incentives));
			})
			.catch((err) => {
				const errorMsg = err.message;
				setPromosState({
					promos: [],
					incentives: [],
					isLoading: false
				});
				trackPerformanceToNR('ws-specials-promos-fetched-with-error');
				addNewRelicPageAction('ws-specials-error', {
					parameters,
					errorMsg
				});
				throw new Error(`${ENDPOINT_FETCH_ERROR}: ${err.message}`);
			});
	}, []);
	return [promosState];
};

export { usePromos };
